import { FEATURE_CONTENT_MAP, type FeatureContent } from "@/lib/features/content";
import { USE_CASE_CONTENT_MAP, type UseCaseContent } from "@/lib/use-cases/content";
import { LOCALES, localizedPath, type Locale, type TranslatedLocale } from "../locales";
import type { HomeContent, LocaleContent, PricingContent } from "./types";
import { EN_HOME } from "./en/home";
import { EN_PRICING } from "./en/pricing";
import { ES_FEATURES } from "./es/features";
import { ES_HOME } from "./es/home";
import { ES_PRICING } from "./es/pricing";
import { ES_USE_CASES } from "./es/use-cases";
import { PT_BR_FEATURES } from "./pt-BR/features";
import { PT_BR_HOME } from "./pt-BR/home";
import { PT_BR_PRICING } from "./pt-BR/pricing";
import { PT_BR_USE_CASES } from "./pt-BR/use-cases";
import { HI_FEATURES } from "./hi/features";
import { HI_HOME } from "./hi/home";
import { HI_PRICING } from "./hi/pricing";
import { HI_USE_CASES } from "./hi/use-cases";
import { ID_FEATURES } from "./id/features";
import { ID_HOME } from "./id/home";
import { ID_PRICING } from "./id/pricing";
import { ID_USE_CASES } from "./id/use-cases";

export type { HomeContent, LocaleContent, PricingContent } from "./types";

export const CONTENT_BY_LOCALE: Record<TranslatedLocale, LocaleContent> = {
  es: { home: ES_HOME, pricing: ES_PRICING, features: ES_FEATURES, useCases: ES_USE_CASES },
  "pt-BR": {
    home: PT_BR_HOME,
    pricing: PT_BR_PRICING,
    features: PT_BR_FEATURES,
    useCases: PT_BR_USE_CASES,
  },
  hi: { home: HI_HOME, pricing: HI_PRICING, features: HI_FEATURES, useCases: HI_USE_CASES },
  id: { home: ID_HOME, pricing: ID_PRICING, features: ID_FEATURES, useCases: ID_USE_CASES },
};

const contentFor = (locale: Locale): LocaleContent | null =>
  locale === "en" ? null : CONTENT_BY_LOCALE[locale];

export const getLocalizedFeature = (slug: string, locale: Locale): FeatureContent | null =>
  contentFor(locale)?.features[slug] ?? FEATURE_CONTENT_MAP[slug] ?? null;

export const getLocalizedUseCase = (slug: string, locale: Locale): UseCaseContent | null =>
  contentFor(locale)?.useCases[slug] ?? USE_CASE_CONTENT_MAP[slug] ?? null;

export const getLocalizedHome = (locale: Locale): HomeContent =>
  contentFor(locale)?.home ?? EN_HOME;

export const getLocalizedPricing = (locale: Locale): PricingContent =>
  contentFor(locale)?.pricing ?? EN_PRICING;

const translatedPathsFor = (content: LocaleContent): string[] => [
  ...(content.home ? ["/"] : []),
  ...(content.pricing ? ["/pricing"] : []),
  ...Object.keys(content.features)
    .filter((slug) => content.features[slug] && FEATURE_CONTENT_MAP[slug])
    .map((slug) => `/features/${slug}`),
  ...Object.keys(content.useCases)
    .filter((slug) => content.useCases[slug] && USE_CASE_CONTENT_MAP[slug])
    .map((slug) => `/use-cases/${slug}`),
];

export const LOCALIZABLE_PATHS = [
  "/",
  "/pricing",
  "/features/auto-clip",
  "/features/ai-captions",
  "/features/find-viral-moments",
  "/features/ai-reframe",
  "/features/video-editor",
  "/use-cases/clippers",
  "/use-cases/podcast-clipping",
  "/use-cases/streamers",
] as const;

const existsInEnglish = (path: string): boolean => {
  if (path === "/" || path === "/pricing") return true;
  const [, hub, slug] = path.split("/");
  if (hub === "features") return Boolean(FEATURE_CONTENT_MAP[slug]);
  if (hub === "use-cases") return Boolean(USE_CASE_CONTENT_MAP[slug]);
  return false;
};

export const TRANSLATED_PATHS: Record<Locale, string[]> = {
  en: LOCALIZABLE_PATHS.filter(existsInEnglish),
  ...(Object.fromEntries(
    LOCALES.map((locale) => [locale, translatedPathsFor(CONTENT_BY_LOCALE[locale])]),
  ) as Record<TranslatedLocale, string[]>),
};

export const hasTranslation = (locale: Locale, path: string): boolean =>
  TRANSLATED_PATHS[locale].includes(path);

export const localesForPath = (path: string): TranslatedLocale[] =>
  LOCALES.filter((locale) => hasTranslation(locale, path));

export const localizedHref = (locale: Locale, path: string): string =>
  hasTranslation(locale, path) ? localizedPath(locale, path) : path;
